import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import useAbortController from 'hooks/useAbortController';
import {
  getHouseholdMembers,
  getHouseholdMembersEligibility,
} from 'services/householdService';
import { AppContext } from 'contexts/AppContext';
import useUserProfileQuery from 'hooks/queries/useUserProfileQuery';
import { getAffordableApplicationAdditionalDetails } from 'services/affordableApplicationAdditionalDetails';
import { sortMembers } from 'pages/AffordaHousehold/utils';
import QUERY_KEYS from 'constants/queryKeys';
import STUDENT_STATUS from 'constants/studentStatus';
import APPLICANT_TYPES from 'constants/applicantTypes';

const useHouseholdMembersQuery = ({ options } = {}) => {
  const { organizationId, propertyId } = useContext(AppContext);
  const { data: { householdId, qualifications } = {} } = useUserProfileQuery();

  const abortControllerOptions = useAbortController();
  const queryKey = QUERY_KEYS.householdMembers({
    organizationId,
    propertyId,
    householdId,
  });

  // memoized this because placeholderData was causing infinite update
  const queryOptions = useMemo(() => {
    const enabled =
      Boolean(organizationId && propertyId && householdId) && options?.enabled;

    return {
      ...options,
      placeholderData: [],
      enabled,
    };
  }, [householdId, options, organizationId, propertyId]);

  return useQuery(
    queryKey,
    async () => {
      const response = await getHouseholdMembers(
        organizationId,
        propertyId,
        householdId,
        abortControllerOptions,
      );

      const memberAdditionalDetails =
        await getAffordableApplicationAdditionalDetails({
          organizationId,
          propertyId,
          householdId,
        });

      const affordableProgramId =
        qualifications?.[0]?.propertyAffordableProgramId;

      const responseQualifications = await getHouseholdMembersEligibility(
        organizationId,
        propertyId,
        householdId,
        affordableProgramId,
        abortControllerOptions,
      );

      const qualificationMembers =
        responseQualifications?.affordableQualifications?.[0]
          ?.eventHouseholdMembers;

      const parsedMembers = (response || []).map(
        ({
          id,
          name,
          affordableRelationshipId,
          applicantTypeId,
          applicationId,
          householdId,
          am,
          ac,
          type: { name: applicantType },
          application: { invitedOtherAdults },
        }) => {
          let member = {
            id,
            name: name?.trim(),
            affordableRelationshipId,
            applicantTypeId,
            applicationId,
            householdId,
            applicantType,
            invitedOtherAdults,
          };

          if (applicantType === APPLICANT_TYPES.MINOR) {
            member = {
              ...member,
              ...am,
              birthday: am.dateOfBirth,
            };
          } else {
            if (ac) {
              const { customer, ...rest } = ac;

              member = {
                ...member,
                ...customer,
                ...rest,
                studentType:
                  rest?.studentType || STUDENT_STATUS.NOT_A_STUDENT?.value,
              };
            }
          }

          const additionalDetails = memberAdditionalDetails?.find(
            ({ applicantId: adApplicantId }) =>
              adApplicantId === member.applicantId,
          );

          const memberEligibility = qualificationMembers?.find(
            ({ applicantId: meApplicantId }) =>
              meApplicantId === member.applicantId,
          );

          return {
            ...member,
            additionalDetails,
            qualifications: memberEligibility,
            ...(additionalDetails?.household && additionalDetails.household),
            id: member.id,
          };
        },
      );

      return sortMembers(parsedMembers);
    },
    queryOptions,
  );
};

export default useHouseholdMembersQuery;
